import { useEffect, useRef, useState } from 'react';
import {
  loadSpotifySdk,
  getDevices,
  pausePlayback,
  startPlayback,
  transferPlayback,
} from '../services/spotifyService';

export default function MusicPlayer({ song, accessToken, onPlayStart }) {
  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [deviceId, setDeviceId] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState('');
  const [deviceStatus, setDeviceStatus] = useState('Initializing Spotify player...');
  const [isDeviceActive, setIsDeviceActive] = useState(false);
  const pollRef = useRef({ timer: null, attempts: 0, inFlight: false });

  useEffect(() => {
    setIsPlaying(false);
  }, [song]);

  useEffect(() => {
    if (!accessToken) return undefined;
    let canceled = false;
    const clearPolling = () => {
      if (pollRef.current.timer) {
        clearTimeout(pollRef.current.timer);
        pollRef.current.timer = null;
      }
      pollRef.current.attempts = 0;
      pollRef.current.inFlight = false;
    };

    const pollForDevice = async (readyDeviceId) => {
      if (pollRef.current.inFlight) return;
      pollRef.current.inFlight = true;

      try {
        const devices = await getDevices(accessToken);
        const hasDevice = devices.some((device) => device.id === readyDeviceId);
        if (hasDevice) {
          await transferPlayback(accessToken, readyDeviceId);
          setDeviceStatus('Device active.');
          setIsDeviceActive(true);
          clearPolling();
          return;
        }
      } catch (err) {
        console.error('Failed to fetch devices:', err);
      } finally {
        pollRef.current.inFlight = false;
      }

      pollRef.current.attempts += 1;
      if (pollRef.current.attempts >= 12) {
        setDeviceStatus('Device not found. Open Spotify once and try again.');
        clearPolling();
        return;
      }

      pollRef.current.timer = setTimeout(() => {
        pollForDevice(readyDeviceId);
      }, 750);
    };

    const setupPlayer = async () => {
      try {
        await loadSpotifySdk();
      } catch (err) {
        if (!canceled) {
          setError('Failed to load Spotify player.');
          console.error(err);
        }
        return;
      }

      if (canceled) return;

      const player = new window.Spotify.Player({
        name: 'Hitster Web Player',
        getOAuthToken: (cb) => cb(accessToken),
        volume: 0.6,
      });

      player.addListener('ready', ({ device_id: readyDeviceId }) => {
        setDeviceId(readyDeviceId);
        setIsReady(true);
        setIsDeviceActive(false);
        setDeviceStatus('Player ready. Activating device...');
        clearPolling();
        pollForDevice(readyDeviceId);
      });

      player.addListener('not_ready', () => {
        setIsReady(false);
        setIsDeviceActive(false);
        setDeviceStatus('Player not ready.');
      });

      player.addListener('player_state_changed', (state) => {
        if (!state) return;
        setIsPlaying(!state.paused);
      });

      player.addListener('initialization_error', ({ message }) => {
        setError(message || 'Failed to initialize Spotify player.');
      });
      player.addListener('authentication_error', ({ message }) => {
        setError(message || 'Spotify authentication error.');
      });
      player.addListener('account_error', ({ message }) => {
        setError(message || 'Spotify account error.');
      });
      player.addListener('playback_error', ({ message }) => {
        setError(message || 'Spotify playback error.');
      });

      player.connect();
      playerRef.current = player;
    };

    setupPlayer();

    return () => {
      canceled = true;
      clearPolling();
      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }
    };
  }, [accessToken]);

  const resolveDeviceId = (devices) => {
    if (!devices.length) return null;
    const exact = devices.find((device) => device.id === deviceId);
    if (exact) return exact.id;
    const byName = devices.find((device) => device.name === 'Hitster Web Player');
    if (byName) return byName.id;
    const active = devices.find((device) => device.is_active);
    if (active) return active.id;
    return devices[0].id;
  };

  const ensureDeviceActive = async () => {
    const devices = await getDevices(accessToken);
    const targetId = resolveDeviceId(devices);
    if (!targetId) {
      setDeviceStatus('Waiting for Spotify to register the web player device...');
      setIsDeviceActive(false);
      return null;
    }

    if (targetId !== deviceId) {
      setDeviceId(targetId);
    }

    await transferPlayback(accessToken, targetId);
    setDeviceStatus('Device active.');
    setIsDeviceActive(true);
    return targetId;
  };


  const togglePlay = async () => {
    if (!deviceId || !accessToken || !song?.uri) {
      setError('Player not ready yet.');
      return;
    }

    setError('');
    try {
      if (isPlaying) {
        await pausePlayback(accessToken);
      } else {
        if (playerRef.current?.activateElement) {
          await playerRef.current.activateElement();
        }
        let targetId = deviceId;
        if (!isDeviceActive || !targetId) {
          targetId = await ensureDeviceActive();
          if (!targetId) {
            throw new Error(
              'Device not found. Open Spotify once (desktop or web) and try again.'
            );
          }
        }
        await startPlayback(accessToken, song.uri, targetId);
        if (onPlayStart) {
          onPlayStart();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Playback failed.');
      console.error(err);
    }
  };

  return (
    <div className="music-player">
      <button
        className={`play-button ${isPlaying ? 'playing' : ''}`}
        onClick={togglePlay}
        disabled={!song?.uri || !isReady}
      >
        {isPlaying ? 'Pause' : 'Play'}
      </button>
      {!isReady && <div className="info-text">Spotify player loading...</div>}
      {isReady && <div className="info-text">{deviceStatus}</div>}
      {error && <div className="error-message">{error}</div>}
    </div>
  );
}
